<template>
  <div class="n-filter" :class="{ 'n-filter--disabled': disabled }">
    <div class="n-filter__count" v-if="showCounter && checked.length > 0">
      <span>{{ checked.length }}</span>
      <button type="button" @click="onFilterReset">
        <span class="visually-hidden"><slot name="resetText">Fjern alle valgte</slot></span>
        <slot name="resetIcon">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" aria-hidden="true">
            <line x1="24" y1="0" x2="0" y2="24" stroke="currentColor" stroke-width="2" fill="none"></line>
            <line x1="0" y1="0" x2="24" y2="24" stroke="currentColor" stroke-width="2" fill="none"></line>
          </svg>
        </slot>
      </button>
    </div>
    <button type="button" class="n-filter__button" :disabled="disabled" :aria-controls="'nfilter-' + _uid + '-list'" :aria-expanded="showList ? 'true' : 'false'" @click="toggleList()">
      <span>
        <template v-if="!multiSelect && selectedItem">
            <span>
                {{ selectedItem[labelKey] }} <i style="font-weight: bold;">{{ selectedItem.taskTemplateId ? '(Opgave oprettes)' : null }}</i>
            </span>
        </template>
        <slot v-else>Filter</slot>
      </span>
      <slot name="buttonIcon">
        <svg class="n-filter__chevron" viewBox="0 0 100 100">
          <g>
            <path d="M10 50h40"/>
            <path d="M90 50H50"/>
          </g>
        </svg>
      </slot>
    </button>
    <div class="n-filter__wrapper" :class="{ 'n-filter__wrapper--hidden': !showList }">
      <div class="n-filter__search" v-if="showSearch">
        <label :for="'nfilter-' + _uid + '-search'" class="visually-hidden"><slot name="searchLabel">Search filter options</slot></label>
        <input type="search" :name="'nfilter-' + _uid + '-search'" :id="'nfilter-' + _uid + '-search'" :placeholder="searchPlaceholder" autocomplete="off" role="combobox" aria-autocomplete="list" :aria-controls="'nfilter-' + _uid + '-list'" v-model="searchText" ref="input" @input="debounceHandler()" @click.stop />
        <!-- <div class="n-filter-search-icon"><slot name="searchIcon">🔍</slot></div> -->
      </div>
      <ul :id="'nfilter-' + _uid + '-list'" class="n-filter__list" role="listbox" ref="list">
        <li class="n-filter__item n-filter__item--empty" v-if="isEmpty"><slot name="empty-options">No options...</slot></li>
        <li class="n-filter__item" v-else v-for="item in list" :key="item[valueKey]" :class="{ 'n-filter-item--checked': isChecked(item) }" :id="item[valueKey]" role="option" :aria-selected="isChecked(item)" :hidden="isMatch(item)" :ref="'item-' + item[valueKey]">
          <input :type="multiSelect ? 'checkbox' : 'radio'" :name="multiSelect ? 'item-' + item[valueKey] : 'nfilter-' + _uid + '-input'" :id="'item-' + item[valueKey]" :checked="isChecked(item)" :value="item[valueKey]" @change="onFilterChange(item, $event)" @keyup.enter="onEnter(item, $event)" />
          <label :for="'item-' + item[valueKey]">{{ item[labelKey] }} <i style="font-weight: bold;">{{ item.taskTemplateId ? '(Opgave oprettes)' : null }}</i></label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { debounce } from '@/mixins/shared/helpers.js'

export default {
    name: 'NFilter',
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: [String, Number, Object, Array],
            default: null
        },
        disabled: {
            type: Boolean
        },
        list: {
            type: Array,
            required: true,
            validator: (prop) => prop.every(item => typeof item === 'object')
        },
        valueKey: {
            type: String,
            default: 'value'
        },
        labelKey: {
            type: String,
            default: 'label'
        },

        multiSelect: {
            type: Boolean,
            default: true
        },
        showSearch: {
            type: Boolean
        },
        searchPlaceholder: {
            type: String,
            default: 'Søg'
        },
        searchMinLength: {
            type: Number,
            default: 1
        },

        showCounter: {
            type: Boolean
        }
    },
    data: () => {
        return {
            showList: false,
            searchText: '',
            checked: []
        }
    },
    computed: {
        getFilteredItems () {
            return this.list.filter(item => item[this.labelKey].toLowerCase().includes(this.searchText.toLowerCase()))
        },
        selectedItem () {
            if (this.multiSelect || this.checked.length === 0) {
                return null
            }

            return this.findMatchingItemByValue(this.checked[0]) || null
        },
        getVisibleItems () {
            return [...this.$refs.list.children].filter(item => !item.hidden)
        },
        isEmpty () {
            if (this.showList) {
                return this.getFilteredItems.length === 0
            }

            return false
        }
    },
    created () {
        this.debounceHandler = debounce(this.onInput, 150)
    },
    watch: {
        value: {
            immediate: true,
            handler (newValue) {
                this.checked = this.resolveSelectedItems(newValue)
            }
        },
        list () {
            this.checked = this.resolveSelectedItems(this.value)
        }
    },
    mounted () {
        if (this.labelKey && this.valueKey) {
            const errorItems = this.list.filter(filter => !Object.keys(filter).includes(this.labelKey) || !Object.keys(filter).includes(this.valueKey))
            if (errorItems.length > 0) {
                console.error('errorItems', errorItems)
                throw new Error('List contains ' + errorItems.length + ' item(s) with the `' + this.labelKey + '` and/or `' + this.valueKey + '` keys missing.')
            }
        } else if (this.labelKey && !this.valueKey) {
            const errorItems = this.list.filter(filter => !Object.keys(filter).includes(this.labelKey) || !Object.keys(filter).includes('value'))
            if (errorItems.length > 0) {
                console.error('errorItems', errorItems)
                throw new Error('List contains ' + errorItems.length + ' item(s) with the `' + this.labelKey + '` and/or `value` keys missing.')
            }
        } else if (!this.labelKey && this.valueKey) {
            const errorItems = this.list.filter(filter => !Object.keys(filter).includes('label') || !Object.keys(filter).includes(this.valueKey))
            if (errorItems.length > 0) {
                console.error('errorItems', errorItems)
                throw new Error('List contains ' + errorItems.length + ' item(s) with the `label` and/or `' + this.valueKey + '` keys missing.')
            }
        } else {
            const errorItems = this.list.filter(filter => !Object.keys(filter).includes('label') || !Object.keys(filter).includes('value'))
            if (errorItems.length > 0) {
                console.error('errorItems', errorItems)
                throw new Error('List contains ' + errorItems.length + ' item(s) with the `label` and/or `value` keys missing.')
            }
        }

        if (!this.$slots.hasOwnProperty('default') || (this.$slots.hasOwnProperty('default') && this.$slots.default.some(property => property.elm && property.elm.nodeName === '#text' && property.text === ''))) {
            throw new Error('Label is missing. Set the label by using the default slot.')
        } else if (this.$slots.hasOwnProperty('default') && this.$slots.default.some(property => property.elm && property.elm.nodeName === 'IMG' && property.elm.attributes.alt.value === '')) {
            throw new Error('Label is missing. Seems like you are using an image, so make sure to set the alt attribute.')
        }

        document.addEventListener('click', this.clickOutside)
    },
    beforeDestroy () {
        this.debounceHandler = null
    },
    destroyed () {
        document.removeEventListener('click', this.clickOutside)
    },
    methods: {
        valuesEqual (left, right) {
            if (left === right) {
                return true
            }

            if (left === null || left === undefined || right === null || right === undefined) {
                return false
            }

            return String(left) === String(right)
        },
        findMatchingItemByValue (value) {
            return this.list.find(item => this.valuesEqual(item[this.valueKey], value))
        },
        toggleList () {
            this.showList = !this.showList
        },

        isMatch (item) {
            if (this.searchText.length >= this.searchMinLength) {
                return !item[this.labelKey].toLowerCase().includes(this.searchText.toLowerCase())
            }

            return false
        },
        isChecked (item) {
            return this.checked.some(checkedValue => this.valuesEqual(checkedValue, item[this.valueKey]))
        },
        onInput () {
            if (this.searchText.length >= this.searchMinLength) {
                this.$refs.list.scrollTop = 0
            }
        },
        resolveSelectedItems (value) {
            const selectedValues = Array.isArray(value) ? value : (value === null || value === undefined || value === '' ? [] : [value])

            if (selectedValues.length === 0) {
                return []
            }

            const selectedIds = []

            selectedValues.forEach((selectedItem) => {
                const selectedValue = typeof selectedItem === 'object' && selectedItem !== null ? selectedItem[this.valueKey] : selectedItem

                const matchedItem = this.findMatchingItemByValue(selectedValue)
                if (matchedItem) {
                    const matchedValue = matchedItem[this.valueKey]
                    const isAlreadySelected = selectedIds.some(id => this.valuesEqual(id, matchedValue))

                    if (!isAlreadySelected) {
                        selectedIds.push(matchedValue)
                    }
                }
            })

            return selectedIds
        },
        emitSelection () {
            const selectedValue = this.multiSelect ? this.checked : (this.checked[0] || null)

            this.$emit('input', selectedValue)
            this.$emit('filterChanged', this.checked)
            this.$emit('optionsChecked', this.checked)
        },
        onFilterChange (item, event) {
            const itemValue = item[this.valueKey]

            if (event.target.checked) {
                if (this.multiSelect) {
                    if (!this.checked.some(checkedValue => this.valuesEqual(checkedValue, itemValue))) {
                        this.checked = [...this.checked, itemValue]
                    }
                } else {
                    this.checked = [itemValue]
                    this.showList = false
                }
            } else {
                this.checked = this.checked.filter(option => !this.valuesEqual(option, itemValue))
            }

            this.emitSelection()
        },
        onEnter (item, event) {
            event.target.checked = !event.target.checked
            this.onFilterChange(item, event)
        },
        onFilterReset () {
            this.searchText = ''
            this.checked = []
            this.emitSelection()
        },

        clickOutside (event) {
            if (this.$el.contains(event.target)) {
                return
            }
            this.showList = false
        }
    }
}
</script>

<style>
.n-filter input[disabled],
.n-filter input[disabled] + label {
  cursor: not-allowed;
}

.n-filter label {
  cursor: pointer;
}

.visually-hidden {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(100%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}
</style>



// WEBPACK FOOTER //
// src/components/base/NFilter.vue